import { z } from 'zod';

import { scheduleIssues } from '../lib/schedule';
import { messages } from '@/shared/constants/messages';

/**
 * Os dois schemas do formulário (spec-project-form.md §7.6), e por que são
 * dois.
 *
 * - `projectDraftSchema` — tudo opcional. É o que o autosave manda, e rascunho
 *   pela metade é estado LEGÍTIMO, não formulário inválido. Um schema só, com
 *   obrigatórios, faria o autosave falhar no primeiro caractere digitado.
 * - `projectSubmitSchema` — o mesmo, endurecido. É ele que habilita o botão de
 *   enviar.
 *
 * A mesma regra vive em três camadas DE PROPÓSITO: aqui (UX imediata),
 * em `submit_project` (mensagem acionável, e a autorização de verdade) e no
 * `projects_submitted_completeness_check` (rede final). Divergência entre elas
 * é bug — é por isso que o pgTAP 009 testa as mesmas fronteiras que o Vitest
 * daqui.
 */

/** Uma linha de apoio: o tipo marcado, e o que se pede dele (opcional). */
export const supportItemSchema = z.object({
  support_type_id: z.string().uuid(),
  request: z.string().trim().max(500).nullable().optional(),
  /** `yyyy-MM-dd` do input nativo de data. */
  needed_by: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
});

export type SupportItemInput = z.infer<typeof supportItemSchema>;

/**
 * O formulário inteiro, como ele vive no React Hook Form.
 *
 * Campo vazio é `null`, nunca `''`: o payload da RPC distingue "não mandei"
 * (chave ausente) de "mandei vazio" (`null`), e string vazia trafegando até o
 * banco viraria um título de zero caractere em vez de campo em branco.
 */
export const projectDraftSchema = z.object({
  ministry_id: z.string().uuid().nullable(),
  title: z.string().trim().max(160).nullable(),
  description: z.string().trim().max(4000).nullable(),
  category_id: z.string().uuid().nullable(),
  /** ISO 8601 com offset, montado por `fromDateAndTimeInputs` (regra 9). */
  starts_at: z.string().datetime({ offset: true }).nullable(),
  ends_at: z.string().datetime({ offset: true }).nullable(),
  expected_audience: z.number().int().positive().nullable(),
  responsible_profile_id: z.string().uuid().nullable(),
  estimated_budget_cents: z.number().int().nonnegative().nullable(),
  funding_source_id: z.string().uuid().nullable(),
  budget_notes: z.string().trim().max(1000).nullable(),
  extra_info: z.string().trim().max(4000).nullable(),
  space_ids: z.array(z.string().uuid()),
  support_items: z.array(supportItemSchema),
});

export type ProjectFormValues = z.infer<typeof projectDraftSchema>;

/**
 * Os obrigatórios da US-15, um a um, com a frase que a tela mostra.
 *
 * **Cada campo repete a mesma frase em `invalid_type_error`**, e isso não é
 * redundância: campo em branco chega como `null`, e sem essa chave o Zod
 * responde com o texto DELE ("Expected string, received null") — jargão em
 * inglês na cara de um voluntário, exatamente o que o RNF-U03 proíbe. É a
 * diferença entre o formulário dizer "dê um título ao projeto" e dizer que
 * esperava uma string.
 */
const requiredText = (message: string) =>
  z.string({ invalid_type_error: message, required_error: message });

export const projectSubmitSchema = projectDraftSchema
  .extend({
    ministry_id: requiredText('Escolha o ministério responsável.').uuid({
      message: 'Escolha o ministério responsável.',
    }),
    title: requiredText('Dê um título ao projeto.')
      .trim()
      .min(1, 'Dê um título ao projeto.')
      .max(160),
    description: requiredText('Descreva o objetivo do projeto.')
      .trim()
      .min(1, 'Descreva o objetivo do projeto.')
      .max(4000),
    category_id: requiredText('Escolha a categoria do projeto.').uuid({
      message: 'Escolha a categoria do projeto.',
    }),
    starts_at: requiredText('Informe a data e a hora de início.').datetime({
      offset: true,
      message: 'Informe a data e a hora de início.',
    }),
    ends_at: requiredText('Informe a data e a hora de término.').datetime({
      offset: true,
      message: 'Informe a data e a hora de término.',
    }),
    responsible_profile_id: requiredText('Escolha quem é a pessoa responsável.').uuid({
      message: 'Escolha quem é a pessoa responsável.',
    }),
    space_ids: z.array(z.string().uuid()).min(1, 'Escolha pelo menos um espaço.'),
  })
  /**
   * A regra de data (`lib/schedule.ts`): início a partir de amanhã, término
   * depois do início. Vem de lá, e não escrita aqui, porque a seção 3 precisa
   * da mesma regra como `min` dos campos nativos — e porque um rascunho que
   * envelheceu na gaveta tem de parar de ser enviável no dia em que a data
   * dele passa, sem ninguém ter tocado no formulário.
   */
  .superRefine((values, ctx) => {
    for (const [path, message] of Object.entries(
      scheduleIssues(values.starts_at, values.ends_at),
    )) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    }
  });

/** Valores iniciais de um formulário em branco — nada de `undefined`. */
export const emptyProjectForm: ProjectFormValues = {
  ministry_id: null,
  title: null,
  description: null,
  category_id: null,
  starts_at: null,
  ends_at: null,
  expected_audience: null,
  responsible_profile_id: null,
  estimated_budget_cents: null,
  funding_source_id: null,
  budget_notes: null,
  extra_info: null,
  space_ids: [],
  support_items: [],
};

/**
 * As cinco seções do documento (§7.1), na ordem em que aparecem na tela.
 *
 * A lista mora aqui, junto do schema, porque é dela que sai o estado de cada
 * seção no índice lateral — e o índice precisa saber quais campos pertencem a
 * qual seção para dizer "falta preencher" no lugar certo. Duas listas
 * separadas divergiriam no primeiro campo que mudasse de seção.
 */
export interface FormSectionSpec {
  id: string;
  number: number;
  title: string;
  /** Campos obrigatórios da seção. Vazio = seção inteiramente opcional. */
  requiredFields: (keyof ProjectFormValues)[];
  optional?: boolean;
}

export const FORM_SECTIONS: readonly FormSectionSpec[] = [
  {
    id: 'identificacao',
    number: 1,
    title: messages.projectSections.identity,
    requiredFields: ['ministry_id', 'title', 'category_id'],
  },
  {
    id: 'objetivo',
    number: 2,
    title: messages.projectSections.objective,
    requiredFields: ['description'],
  },
  {
    id: 'data-e-local',
    number: 3,
    title: messages.projectSections.schedule,
    requiredFields: ['starts_at', 'ends_at', 'space_ids'],
  },
  {
    id: 'apoio-e-custos',
    number: 4,
    title: messages.projectSections.support,
    requiredFields: [],
    optional: true,
  },
  {
    id: 'responsavel-e-revisao',
    number: 5,
    title: messages.projectSections.review,
    requiredFields: ['responsible_profile_id'],
  },
];

export type SectionState = 'empty' | 'partial' | 'complete';

/**
 * Estado de uma seção para o índice: vazia, faltando algo, ou completa.
 *
 * Seção opcional nunca é "faltando" — ela é `complete` quando tem alguma
 * coisa e `empty` quando não tem, e é isso que impede o índice de acusar
 * pendência numa seção que a spec declara opcional.
 */
export function sectionState(
  section: FormSectionSpec,
  values: ProjectFormValues,
): SectionState {
  const filled = (field: keyof ProjectFormValues): boolean => {
    const value = values[field];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== null && value !== undefined;
  };

  if (section.requiredFields.length === 0) {
    // Seção 4: qualquer sinal de preenchimento conta como pronta.
    const touched =
      values.support_items.length > 0 ||
      values.estimated_budget_cents !== null ||
      values.funding_source_id !== null ||
      Boolean(values.budget_notes?.trim());
    return touched ? 'complete' : 'empty';
  }

  const done = section.requiredFields.filter(filled).length;
  if (done === 0) return 'empty';
  return done === section.requiredFields.length ? 'complete' : 'partial';
}
